import React, { useEffect, useMemo, useState } from "react";
import { fetchSiengePermissions, checkSiengePermissions } from "./siengePermissionsApi";

const fmtDateTime = (d) => (d ? new Date(d).toLocaleString("pt-BR") : "nunca verificado");

// Célula de um escopo: liberado / falta / desconhecido, com destaque do que mudou na
// última verificação (verde pulsante = liberado desde a última vez; vermelho = revogado).
const ScopeCell = ({ scope, changed }) => {
  if (!scope || scope.allowed === null) {
    return <td className="scope-cell"><span className="scope-pill scope-unknown" title="Não foi possível verificar (rede/limite) — verifique de novo">?</span></td>;
  }
  const cls = scope.allowed ? "scope-yes" : "scope-no";
  const changeCls = changed === "granted" ? " scope-just-granted" : changed === "revoked" ? " scope-just-revoked" : "";
  return (
    <td className="scope-cell">
      <span className={`scope-pill ${cls}${changeCls}`} title={scope.usage}>
        {scope.allowed ? "liberado" : "falta"}
        {changed === "granted" && " ✓ novo"}
        {changed === "revoked" && " ⚠ revogado"}
      </span>
    </td>
  );
};

const ClientRow = ({ client, catalog, onProbed }) => {
  const [checking, setChecking] = useState(false);
  const [row, setRow] = useState(client);

  const onCheck = async () => {
    setChecking(true);
    const res = await checkSiengePermissions(String(client._id));
    setChecking(false);
    if (res?.scopes) {
      const updated = {
        ...row,
        scopes: res.scopes,
        denied: res.denied,
        last_change: res.last_change,
        probed_at: res.probed_at,
      };
      setRow(updated);
      onProbed?.(updated);
    }
  };

  const changeOf = (key) => {
    if (row.last_change?.granted?.includes(key)) return "granted";
    if (row.last_change?.revoked?.includes(key)) return "revoked";
    return null;
  };

  return (
    <tr>
      <td>
        <b>{row.name}</b>
        {row.subdomain ? <span className="sienge-perms-sub">{row.subdomain}</span> : null}
      </td>
      {catalog.map((col) => (
        <ScopeCell key={col.key} scope={row.scopes?.[col.key]} changed={changeOf(col.key)} />
      ))}
      <td className="sienge-perms-when">{fmtDateTime(row.probed_at)}</td>
      <td>
        <button type="button" disabled={checking} onClick={onCheck}>
          {checking ? "Verificando…" : row.probed_at ? "Reverificar" : "Verificar"}
        </button>
      </td>
    </tr>
  );
};

const SiengePermissions = () => {
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [onlyMissing, setOnlyMissing] = useState(false);

  useEffect(() => {
    fetchSiengePermissions().then(setData);
  }, []);

  const catalog = data?.catalog ?? [];
  const clients = data?.clients ?? [];

  const onProbed = (updated) => {
    setData((d) => ({
      ...d,
      clients: d.clients.map((c) => (String(c._id) === String(updated._id) ? updated : c)),
    }));
  };

  const visible = useMemo(
    () =>
      clients.filter((c) => {
        if (query && !c.name?.toLowerCase().includes(query.toLowerCase())) return false;
        if (onlyMissing && !(c.denied && c.denied.length > 0)) return false;
        return true;
      }),
    [clients, query, onlyMissing]
  );

  // Cobertura por API (quantos clientes já verificados têm cada escopo liberado),
  // agrupada pela categoria do painel do Sienge.
  const coverageGroups = useMemo(() => {
    const probed = clients.filter((c) => c.scopes);
    const byGroup = {};
    for (const col of catalog) {
      const g = col.group || "Outros";
      (byGroup[g] ??= []).push({
        ...col,
        allowed: probed.filter((c) => c.scopes?.[col.key]?.allowed === true).length,
        total: probed.length,
      });
    }
    return Object.entries(byGroup);
  }, [clients, catalog]);
  const anyProbed = clients.some((c) => c.scopes);

  return (
    <div className="sienge-perms-page integrations-page">
      <h2>Permissões da API Sienge</h2>
      <p className="margin-bottom-16">
        Ferramenta interna — o que a API do Sienge de cada cliente libera para o Ampli. Clique em
        <b> Verificar</b> para sondar ao vivo (o Sienge não expõe as permissões; sondamos com uma
        chamada mínima por API). O resultado fica registrado; ao reverificar, o que mudou desde a
        última vez aparece destacado.
      </p>

      {anyProbed && (
        <div className="sienge-perms-coverage">
          {coverageGroups.map(([group, items]) => (
            <div key={group} className="sienge-perms-cov-group">
              <span className="sienge-perms-cov-grouplabel">{group}</span>
              <div className="sienge-perms-cov-items">
                {items.map((c) => (
                  <div key={c.key} className="sienge-perms-cov-item" title={c.usage}>
                    <span className="sienge-perms-cov-label">{c.label}</span>
                    <span className="sienge-perms-cov-val">{c.allowed}/{c.total}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="integrations-controls">
        <input
          type="search"
          placeholder="Filtrar por cliente…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <label className="sienge-perms-onlymissing">
          <input type="checkbox" checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} />
          só com pendências
        </label>
      </div>

      {data === null ? (
        <p>Carregando…</p>
      ) : (
        <div className="integrations-scroll">
          <table className="integrations-table sienge-perms-table">
            <thead>
              <tr>
                <th>Cliente</th>
                {catalog.map((col) => (
                  <th key={col.key} className="scope-cell" title={col.usage}>{col.label}</th>
                ))}
                <th className="sienge-perms-when">Última verificação</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visible.map((c) => (
                <ClientRow key={String(c._id)} client={c} catalog={catalog} onProbed={onProbed} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SiengePermissions;
